import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { contractorSubscriptionService, type ContractorPlan } from '../services/contractorSubscriptionService';
import { contractorAuthService } from '../services/contractorAuthService';
import { SafeHtml } from '../utils/safeHtml';

export const ContractorSubscriptionManagement: React.FC = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Subscription data
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [hasSubscription, setHasSubscription] = useState(false);

  // Payment method dialog
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Plan selection dialog
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [contractorPlans, setContractorPlans] = useState<ContractorPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<ContractorPlan | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Cancel dialog
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    checkAuthAndLoadSubscription();
  }, []);

  const checkAuthAndLoadSubscription = async () => {
    try {
      // Check if contractor is authenticated
      const contractorToken = localStorage.getItem('contractor_token');
      if (!contractorToken) {
        navigate('/contractor/auth');
        return;
      }

      // Verify token
      const verification = await contractorAuthService.verifyToken(contractorToken);
      if (!verification.valid) {
        contractorAuthService.logout();
        navigate('/contractor/auth');
        return;
      }

      // Load subscription data
      await loadSubscription();
    } catch (err: any) {
      console.error('Auth check error:', err);
      setError('Authentication failed');
      setTimeout(() => navigate('/contractor/auth'), 2000);
    }
  };

  const loadSubscription = async () => {
    try {
      setLoading(true);
      const contractorInfo = contractorAuthService.getContractorInfo();
      if (!contractorInfo) {
        throw new Error('No contractor info found');
      }

      const data = await contractorSubscriptionService.getSubscription(contractorInfo.email);
      setSubscriptionData(data);
      setHasSubscription(data.hasSubscription);
    } catch (err: any) {
      console.error('Error loading subscription:', err);
      setError(err.response?.data?.error || 'Failed to load subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    if (!stripe || !elements) {
      setPaymentError('Stripe not loaded');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setPaymentError('Card element not found');
      return;
    }

    try {
      setPaymentLoading(true);
      setPaymentError(null);

      const contractorInfo = contractorAuthService.getContractorInfo();
      if (!contractorInfo) {
        throw new Error('No contractor info found');
      }

      // Create payment method
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          email: contractorInfo.email,
          name: contractorInfo.name,
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (!paymentMethod) {
        throw new Error('Failed to create payment method');
      }

      // Update payment method on server
      await contractorSubscriptionService.updatePaymentMethod(
        contractorInfo.email,
        paymentMethod.id
      );

      setSuccess('Payment method updated successfully!');
      setShowPaymentDialog(false);
      await loadSubscription();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Payment method update error:', err);
      setPaymentError(err.message || 'Failed to update payment method');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleOpenPlanDialog = async () => {
    try {
      const plans = await contractorSubscriptionService.getPlans();
      setContractorPlans(plans);
      setSelectedPlan(plans[0] || null);
      setShowPlanDialog(true);
    } catch (err: any) {
      console.error('Error loading plans:', err);
      setError('Failed to load plans');
    }
  };

  const handleSubscribe = async () => {
    if (!selectedPlan || !stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setPaymentError('Card element not found');
      return;
    }

    try {
      setSubscriptionLoading(true);
      setPaymentError(null);

      const contractorInfo = contractorAuthService.getContractorInfo();
      if (!contractorInfo) {
        throw new Error('No contractor info found');
      }

      // Create payment method
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          email: contractorInfo.email,
          name: contractorInfo.name,
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (!paymentMethod) {
        throw new Error('Failed to create payment method');
      }

      // Create subscription
      await contractorSubscriptionService.createSubscription({
        email: contractorInfo.email,
        planId: selectedPlan._id,
        paymentMethodId: paymentMethod.id,
        billingCycle: billingCycle
      });

      setSuccess('Subscription activated successfully!');
      setShowPlanDialog(false);
      await loadSubscription();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Subscription error:', err);
      setPaymentError(err.response?.data?.error || 'Failed to create subscription');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCancelLoading(true);
      const contractorInfo = contractorAuthService.getContractorInfo();
      if (!contractorInfo) {
        throw new Error('No contractor info found');
      }

      await contractorSubscriptionService.cancelSubscription(contractorInfo.email);

      setSuccess('Subscription cancelled successfully');
      setShowCancelDialog(false);
      await loadSubscription();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Cancel error:', err);
      setError(err.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const getPlanPrice = (plan: ContractorPlan) => {
    const price = billingCycle === 'annual' ? plan.annualFee : plan.monthlyFee;
    if (price === undefined || price === null || isNaN(price)) {
      return 'Price not available';
    }
    return `AU$${price.toFixed(2)}`;
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading subscription...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button onClick={() => navigate('/contractor/dashboard')}>
          ← Back to Dashboard
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom fontWeight="bold">
        Subscription Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Current Subscription Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Subscription
          </Typography>
          <Divider sx={{ my: 2 }} />

          {hasSubscription ? (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={subscriptionData.subscriptionStatus}
                    color={subscriptionData.subscriptionStatus === 'active' ? 'success' : 'default'}
                    icon={subscriptionData.subscriptionStatus === 'active' ? <CheckIcon /> : undefined}
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Jobs
                  </Typography>
                  <Typography variant="h6">
                    {subscriptionData.totalJobsAssigned}
                  </Typography>
                </Grid>
                {subscriptionData.subscriptionEndDate && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Valid Until
                    </Typography>
                    <Typography variant="body1">
                      {new Date(subscriptionData.subscriptionEndDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setShowCancelDialog(true)}
                  disabled={subscriptionData.subscriptionStatus !== 'active'}
                >
                  Cancel Subscription
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                You are currently on the free plan with 3 free quotes.
              </Alert>
              {(() => {
                const limit = (subscriptionData?.freeQuoteLimit ?? 3);
                const used = (subscriptionData?.totalQuoteViews ?? (
                  (subscriptionData?.freeQuoteLimit != null && subscriptionData?.freeQuoteRemaining != null)
                    ? (subscriptionData.freeQuoteLimit - subscriptionData.freeQuoteRemaining)
                    : 0
                ));
                return (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Free quote views: {Math.max(0, used)} / {limit}
                  </Typography>
                );
              })()}
              <Button
                variant="contained"
                onClick={handleOpenPlanDialog}
                sx={{ mt: 2 }}
              >
                Subscribe Now
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Payment Method Card */}
      {hasSubscription && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payment Method
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CreditCardIcon sx={{ mr: 2, color: 'text.secondary' }} />
              <Box>
                {subscriptionData.paymentMethod ? (
                  <>
                    <Typography variant="body1">
                      {subscriptionData.paymentMethod.brand.charAt(0).toUpperCase() + 
                       subscriptionData.paymentMethod.brand.slice(1)} •••• {subscriptionData.paymentMethod.last4}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Expires {subscriptionData.paymentMethod.expMonth}/{subscriptionData.paymentMethod.expYear}
                    </Typography>
                  </>
                ) : subscriptionData.stripePaymentMethodId ? (
                  <Typography>Card on file</Typography>
                ) : (
                  <Typography color="text.secondary">No payment method</Typography>
                )}
              </Box>
            </Box>

            <Button
              variant="outlined"
              onClick={() => {
                setPaymentError(null);
                setShowPaymentDialog(true);
              }}
            >
              Update Payment Method
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Update Payment Method Dialog */}
      <Dialog 
        open={showPaymentDialog} 
        onClose={() => !paymentLoading && setShowPaymentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Payment Method</DialogTitle>
        <DialogContent>
          {paymentError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {paymentError}
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Card Information
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                '& .StripeElement': {
                  width: '100%',
                },
              }}
            >
              <CardElement
                options={{
                  hidePostalCode: true,
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                    invalid: {
                      color: '#9e2146',
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentDialog(false)} disabled={paymentLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdatePaymentMethod}
            variant="contained"
            disabled={!stripe || !elements || paymentLoading}
          >
            {paymentLoading ? <CircularProgress size={24} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subscribe Dialog */}
      <Dialog
        open={showPlanDialog}
        onClose={() => !subscriptionLoading && setShowPlanDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Subscribe to Contractor Plan</DialogTitle>
        <DialogContent>
          {paymentError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPaymentError(null)}>
              {paymentError}
            </Alert>
          )}

          {/* Billing Cycle Toggle */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
              <Typography>Monthly</Typography>
              <Button
                variant={billingCycle === 'monthly' ? 'contained' : 'outlined'}
                onClick={() => setBillingCycle('monthly')}
                size="small"
              >
                Monthly
              </Button>
              <Button
                variant={billingCycle === 'annual' ? 'contained' : 'outlined'}
                onClick={() => setBillingCycle('annual')}
                size="small"
              >
                Annual (Save 17%)
              </Button>
              <Typography>Annual</Typography>
            </Stack>
          </Box>

          {/* Plans */}
          <Grid container spacing={2}>
            {contractorPlans.map((plan) => (
              <Grid item xs={12} key={plan._id}>
                <Paper
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: 2,
                    borderColor: selectedPlan?._id === plan._id ? 'primary.main' : 'transparent',
                  }}
                  onClick={() => setSelectedPlan(plan)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h6">{plan.name}</Typography>
                      {plan.description && (
                        <Box sx={{ mt: 1 }}>
                          <SafeHtml 
                            content={plan.description}
                            style={{ 
                              fontSize: '14px',
                              lineHeight: 1.5,
                              color: '#666'
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                    <Typography variant="h5" color="primary">
                      {getPlanPrice(plan)}
                      <Typography variant="caption" display="block">
                        per {billingCycle === 'annual' ? 'year' : 'month'}
                      </Typography>
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Payment Method */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payment Information
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                mt: 2,
              }}
            >
              <CardElement
                options={{
                  hidePostalCode: true,
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                    invalid: {
                      color: '#9e2146',
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPlanDialog(false)} disabled={subscriptionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubscribe}
            variant="contained"
            disabled={!selectedPlan || !stripe || !elements || subscriptionLoading}
          >
            {subscriptionLoading ? <CircularProgress size={24} /> : 'Subscribe'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={showCancelDialog}
        onClose={() => !cancelLoading && setShowCancelDialog(false)}
      >
        <DialogTitle>Cancel Subscription?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel your subscription? You will lose access to unlimited jobs at the end of your billing period.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)} disabled={cancelLoading}>
            Keep Subscription
          </Button>
          <Button
            onClick={handleCancelSubscription}
            color="error"
            variant="contained"
            disabled={cancelLoading}
          >
            {cancelLoading ? <CircularProgress size={24} /> : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

